import { ErrorMessage, useField } from 'formik';
import { memo, useCallback, useRef } from 'react';
import { ChevronDownIcon } from 'lucide-react';

import { CustomErrorMessage } from '@src/design-system/error-message';
import { cn } from '@src/design-system/helpers';
import { inputVariants } from '@src/design-system/input';
import { Timezones } from './timezones';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandList,
} from '@src/design-system/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import { useGetAllTimezones } from '@shared/services/get-all-timezones';

export const TimezoneField = memo(
  ({
    selectPlaceholder = 'Select timezone',
    selectName,
    asInput = true,
  }: {
    selectPlaceholder?: string;
    selectName: string;
    asInput?: boolean;
  }) => {
    const [selectFieldProps] = useField(selectName);
    const { data: timezones } = useGetAllTimezones();
    const listRef = useRef<HTMLDivElement>(null);

    const scrollListToTop = useCallback(() => {
      const scroll = () => {
        if (listRef.current) {
          listRef.current.scrollTop = 0;
        }
      };
      scroll();
      requestAnimationFrame(scroll);
      setTimeout(scroll, 0);
    }, []);

    const selectedTimezone = timezones?.find(
      (t) => t === selectFieldProps.value
    );

    return (
      <>
        <Popover modal>
          <PopoverTrigger
            className={
              asInput
                ? cn(
                    inputVariants(),
                    'w-full flex justify-between focus-visible:z-10 focus-within:z-10 focus-within:border-l items-center'
                  )
                : 'flex gap-2 text-gray-600 font-medium text-sm'
            }
          >
            <span
              className={cn(
                'text-sm',
                !selectFieldProps.value && 'text-gray-500'
              )}
            >
              {selectFieldProps.value ? selectedTimezone : selectPlaceholder}
            </span>

            <ChevronDownIcon size={20} />
          </PopoverTrigger>
          <PopoverContent
            withPortal={false}
            style={{
              width: 'var(--radix-popover-trigger-width)',
              minWidth: 200,
            }}
            align='end'
            className='p-2'
          >
            <Command>
              <CommandInput
                placeholder='Search timezones...'
                onValueChange={scrollListToTop}
              />
              <CommandList ref={listRef}>
                <CommandEmpty>No results found.</CommandEmpty>
                <Timezones
                  timezones={timezones || []}
                  name={selectName}
                />
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <ErrorMessage
          name={selectFieldProps.name}
          component={CustomErrorMessage}
        />
      </>
    );
  }
);
